/**
 * Sentry error tracking for the live-avatar frontend.
 * Captures unhandled exceptions, performance, and session replays.
 * Logz.io remains the primary structured logging sink.
 */
import * as Sentry from "@sentry/react";
import { tabId } from "./frontend-logger";

const APP_NAME = "live-avatar-front";
const APP_VERSION = "1.0.0";

// ─── Environment detection ─────────────────────────────────────────────────

function detectDeploymentEnv(): string {
  if (typeof window === "undefined") return "server";
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "development";
  if (host === "live-ai-avatars.powtoon.com") return "powtoon-production";
  if (host === "live-ai-avatars-staging.powtoon.com") return "powtoon-staging";
  if (host === "live-ai-avatars-dev.powtoon.com") return "powtoon-dev";
  if (host.endsWith(".lovable.app")) return "lovable-preview";
  return "unknown";
}

// ─── Init ───────────────────────────────────────────────────────────────────

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    console.warn("[Sentry] No VITE_SENTRY_DSN — Sentry disabled");
    return;
  }

  const deploymentEnv = detectDeploymentEnv();
  const isProd = deploymentEnv === "powtoon-production";

  Sentry.init({
    dsn,
    environment: deploymentEnv,
    release: `${APP_NAME}@${APP_VERSION}`,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: isProd ? 0.5 : 1.0,
    replaysSessionSampleRate: isProd ? 0.05 : 0.1,
    replaysOnErrorSampleRate: 1.0,
    initialScope: {
      tags: {
        appName: APP_NAME,
        appVersion: APP_VERSION,
        deployment: deploymentEnv,
        supabaseProject: import.meta.env.VITE_SUPABASE_URL
          ? new URL(import.meta.env.VITE_SUPABASE_URL as string).hostname.split(".")[0]
          : "unknown",
      },
    },
    beforeSend(event, hint) {
      if (event.request?.url) {
        event.request.url = event.request.url.replace(
          /(__lovable_token|access_token|token)=[^&]*/g,
          "$1=[REDACTED]",
        );
      }

      const originalException = hint?.originalException;
      const errorMessage =
        originalException instanceof Error
          ? originalException.message
          : typeof originalException === "string"
            ? originalException
            : "";

      if (
        errorMessage.includes("ChunkLoadError") ||
        errorMessage.includes("NetworkError") ||
        errorMessage.includes("Load failed")
      ) {
        return null;
      }

      if (import.meta.env.DEV && errorMessage.includes("must be used within")) {
        return null;
      }

      return event;
    },
  });

  Sentry.setTag("tabId", tabId);

  if (deploymentEnv === "development") {
    (window as unknown as Record<string, unknown>).testSentryError = () => {
      throw new Error("Test Sentry integration");
    };
  }
}

// ─── User context ───────────────────────────────────────────────────────────

export function setSentryUser(powtoonUserId: string | number, email?: string): void {
  Sentry.setUser({
    id: String(powtoonUserId),
    ...(email && { email }),
  });
  Sentry.setTag("powtoonUserId", String(powtoonUserId));
}

export function clearSentryUser(): void {
  Sentry.setUser(null);
}
